import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { pageview } from "@/lib/analytics";

// The public landing is a standalone HTML document served by the host at "/".
// The React app never owns "/"; if the bundle ever loads there, fall back to the
// landing document itself (not "/") so we never bounce in a reload loop.
const HardHome = () => {
  useEffect(() => {
    const target = window.location.pathname === "/" ? "/landing/index.html" : "/";
    window.location.replace(target);
  }, []);
  return null;
};

// Keep the existing app routes split into their own bundles.
const SignIn = lazy(() => import("@/pages/SignIn"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Series = lazy(() => import("@/pages/Series"));
const Watch = lazy(() => import("@/pages/Watch"));
const Admin = lazy(() => import("@/pages/Admin"));
const AlgoDock = lazy(() =>
  import("@/components/AlgoDock").then((m) => ({ default: m.AlgoDock }))
);

const Checking = () => (
  <div className="flex min-h-screen items-center justify-center bg-ink">
    <div className="skeleton h-3 w-40" />
  </div>
);

const PublicOnly = ({ children, redirectTo = "/home" }) => {
  const { user } = useAuth();
  if (user === null) return <Checking />;
  if (user) return <Navigate to={redirectTo} replace />;
  return children;
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Checking />;
  if (!user) return <Navigate to="/access" replace />;
  return (
    <>
      {children}
      <Suspense fallback={null}>
        <AlgoDock />
      </Suspense>
    </>
  );
};

// Sends a GA4 page_view on every route change (config runs with send_page_view:false).
const RouteTracker = () => {
  const { pathname, search } = useLocation();
  useEffect(() => { pageview(pathname + search); }, [pathname, search]);
  return null;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouteTracker />
        <Suspense fallback={<Checking />}>
          <Routes>
            <Route path="/admin-login" element={<PublicOnly redirectTo="/home"><SignIn initial="admin" /></PublicOnly>} />
            <Route path="/access" element={<PublicOnly><SignIn /></PublicOnly>} />
            <Route path="/signup" element={<Navigate to="/access" replace />} />
            <Route path="/login" element={<Navigate to="/access" replace />} />
            <Route path="/home" element={<Protected><Gallery /></Protected>} />
            <Route path="/series/:slug" element={<Protected><Series /></Protected>} />
            <Route path="/watch/:episodeId" element={<Protected><Watch /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="*" element={<HardHome />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
